import setupFocusTrap from './focus-trap';
import getNewIndex from './get-new-index';
import setupElementAnimation from './element-animation';

let carouselCount = 0;

export function setupCarousel( carousel, options = {} ) {
	carouselCount++;
	const idPrefix = `carousel-${ carouselCount }`;

	// Required elements
	const slides = carousel.querySelectorAll( '.js-carousel-slide' );
	const track = carousel.querySelector( '.js-carousel-track' );

	// Optional elements
	const prevButton = carousel.querySelector( '.js-carousel-prev' );
	const nextButton = carousel.querySelector( '.js-carousel-next' );
	const pagination = carousel.querySelector( '.js-carousel-pagination' );
	const captionBox = carousel.querySelector( '.js-carousel-caption' );
	const indicator = carousel.querySelector( '.js-carousel-indicator' );
	const currentIndicator = carousel.querySelector( '.js-carousel-current' );
	const totalIndicator = carousel.querySelector( '.js-carousel-total' );
	let paginationButtons, slideCaptions;

	let currentIndex = 0;

	// Get ID for the track
	let trackId = track.id;
	if ( ! trackId ) {
		trackId = `${ idPrefix }-track`;
		track.id = trackId;
	}

	// Setup indicator as a progress bar
	if ( indicator ) {
		indicator.setAttribute( 'role', 'progressbar' );
		indicator.setAttribute( 'aria-label', 'Slide Progress' );
		indicator.setAttribute( 'aria-valuenow', currentIndex + 1 );
		indicator.setAttribute( 'aria-valuetext', currentIndex + 1 );
		indicator.setAttribute( 'aria-valuemax', slides.length );
	}
	if ( currentIndicator ) {
		currentIndicator.innerText = currentIndex;
	}
	if ( totalIndicator ) {
		totalIndicator.innerText = slides.length;
	}

	slides.forEach( ( slide, index ) => {
		slide.setAttribute( 'role', 'group' );
		slide.setAttribute( 'aria-roledescription', 'slide' );
		slide.setAttribute( 'aria-hidden', true );

		let slideId = slide.id;
		if ( ! slideId ) {
			slideId = `${ idPrefix }-slide-${ index + 1 }`;
			slide.id = slideId;
		}

		// Add pagination dot for slide if applicable
		if ( pagination ) {
			// Pagination is treated as tabs
			slide.setAttribute( 'role', 'tabpanel' );

			const button = document.createElement( 'button' );
			button.type = 'button';
			button.tabIndex = -1;
			button.setAttribute( 'role', 'tab' );
			button.setAttribute( 'aria-controls', slideId );
			button.setAttribute( 'aria-selected', false );
			button.classList.add( 'js-carousel-pagination-button' );
			button.innerText = index + 1;

			pagination.append( button );
		}

		// Add caption entry for slide if applicable
		const slideCaption = slide.querySelector( '.js-slide-caption' );
		if ( captionBox && slideCaption ) {
			let captionId = slideCaption.id;
			if ( ! captionId ) {
				captionId = `${ slideId }-caption`;
				slideCaption.id = captionId;
			}

			slide.setAttribute( 'aria-labelledby', captionId );
			slideCaption.setAttribute( 'aria-hidden', true );
			captionBox.append( slideCaption );
		}
	} );

	// Get pagination buttons
	if ( pagination ) {
		track.setAttribute( 'role', 'tablist' );
		paginationButtons = pagination.querySelectorAll( '.js-carousel-pagination-button' );
	}
	// Get captions
	if ( captionBox ) {
		slideCaptions = captionBox.querySelectorAll( '.js-slide-caption' );
	}

	function goToSlide( newIndex ) {
		// Get the new index if just next/prev boolean
		if ( typeof newIndex !== 'number' ) {
			newIndex = getNewIndex( currentIndex, slides.length, newIndex );
		}

		// Determine direction
		let backward = newIndex < currentIndex;
		// last > first = forward
		if ( newIndex === 0 && currentIndex === slides.length - 1 ) {
			backward = false;
		}
		// first > last = backward
		if ( newIndex === slides.length - 1 && currentIndex === 0 ) {
			backward = true;
		}

		// Update slides
		const currentSlide = slides.item( currentIndex );
		const newSlide = slides.item( newIndex );
		if ( options.animate ) {
			setupElementAnimation( slides.item( newIndex ), true, backward );
			setupElementAnimation( slides.item( currentIndex ), false, backward );
		}
		currentSlide.setAttribute( 'aria-hidden', true );
		newSlide.setAttribute( 'aria-hidden', false );

		// Update buttons if present
		if ( paginationButtons ) {
			const currentTab = paginationButtons.item( currentIndex );
			currentTab.tabIndex = -1;
			currentTab.setAttribute( 'aria-selected', false );

			const newTab = paginationButtons.item( newIndex );
			newTab.tabIndex = 0;
			newTab.setAttribute( 'aria-selected', true );
		}

		// Update caption if present
		if ( slideCaptions ) {
			const currentCaption = slideCaptions.item( currentIndex );
			const newCaption = slideCaptions.item( newIndex );
			if ( options.animate ) {
				setupElementAnimation( slideCaptions.item( newIndex ), true, backward );
				setupElementAnimation( slideCaptions.item( currentIndex ), false, backward );
			}
			currentCaption.setAttribute( 'aria-hidden', true );
			newCaption.setAttribute( 'aria-hidden', false );
		}

		// Fire callback if provided
		if ( typeof options.onSlideChange === 'function' ) {
			options.onSlideChange( newSlide, currentSlide );
		}

		// Update current index
		currentIndex = newIndex;

		// Update the indicator
		if ( indicator ) {
			indicator.setAttribute( 'aria-valuenow', currentIndex + 1 );
			indicator.setAttribute( 'aria-valuetext', currentIndex + 1 );
		}
		if ( currentIndicator ) {
			currentIndicator.innerText = currentIndex;
		}
	}

	// Next/Prev button functionality
	if ( nextButton ) {
		nextButton.setAttribute( 'aria-controls', trackId );
		nextButton.addEventListener( 'click', event => {
			event.preventDefault();
			goToSlide( true );
		} );
	}
	if ( prevButton ) {
		prevButton.setAttribute( 'aria-controls', trackId );
		prevButton.addEventListener( 'click', event => {
			event.preventDefault();
			goToSlide( false );
		} );
	}

	// Pagination button functionality
	if ( paginationButtons ) {
		pagination.addEventListener( 'click', event => {
			const index = [ ...paginationButtons ].indexOf( event.target );
			if ( index >= 0 ) {
				goToSlide( index );
			}
		} );

		// Allow navigation pagination buttons with arrow keys
		setupFocusTrap( pagination, paginationButtons, {
			arrowKeys: true,
			assumeFocusable: true,
		}, goToSlide );
	}

	// Initialize first slide
	slides.item( 0 ).setAttribute( 'aria-hidden', false );
	if ( paginationButtons ) {
		paginationButtons.item( 0 ).tabIndex = 0;
		paginationButtons.item( 0 ).setAttribute( 'aria-selected', true );
	}
	if ( slideCaptions ) {
		slideCaptions.item( 0 ).setAttribute( 'aria-hidden', false );
	}
}

export default function setupCarousels( options ) {
	document.querySelectorAll( '.js-carousel' ).forEach( carousel => setupCarousel( carousel, options ) );
}
